import { useState } from 'react';
import { Crown, Fish } from 'lucide-react';
import { useCraqueBagre } from '../hooks/useCraqueBagre';
import type { FutebolEventoPartida, DadosJogadorHT } from '@/shared/types';

// ── Foto do jogador com fallback ──────────────────────────────────────────────

function FotoJogador({ src, nome, size = 80 }: { src: string | null; nome: string; size?: number }) {
  const [erro, setErro] = useState(false);
  if (!src || erro) {
    return (
      <div
        className="rounded-full bg-white/10 flex items-center justify-center font-bold text-white/50 uppercase shrink-0"
        style={{ width: size, height: size, fontSize: size * 0.28 }}
      >
        {nome.slice(0, 2)}
      </div>
    );
  }
  return (
    <img
      src={src} alt={nome}
      className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size }}
      onError={() => setErro(true)}
    />
  );
}

// ── Estrelas de nota ──────────────────────────────────────────────────────────

function NotaStars({ nota }: { nota: number }) {
  const pct = Math.round((nota / 10) * 5);
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} className={`w-3 h-3 ${i < pct ? 'text-amber-400' : 'text-white/15'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="ml-1 text-[10px] font-bold text-amber-400">{nota.toFixed(1)}</span>
    </div>
  );
}

// ── Card do jogador (craque) ──────────────────────────────────────────────────

function CardCraque({ jogador }: { jogador: DadosJogadorHT }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <FotoJogador src={jogador.fotoUrl} nome={jogador.nome} size={88} />
        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
          <Crown size={13} className="text-black" fill="currentColor" strokeWidth={0} />
        </span>
      </div>
      <div className="text-center">
        <p className="text-white font-bold text-sm leading-tight">{jogador.nome}</p>
        <NotaStars nota={jogador.nota} />
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

interface Props {
  telaId:       string;
  fixtureId:    number | null;
  ultimoEvento: FutebolEventoPartida;
}

export default function MobileCraqueBagre({ telaId, fixtureId, ultimoEvento }: Props) {
  const { dados, votado, pctConcorda, totalVotos, votar } = useCraqueBagre(telaId, fixtureId, ultimoEvento);

  if (!dados) return null;

  const pctDiscorda = 100 - pctConcorda;

  return (
    <div
      className="animate-mcb-entrada rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #0d0a02 0%, #12100a 100%)',
        border:     '1.5px solid rgba(251,191,36,0.35)',
      }}
    >
      {/* Cabeçalho */}
      <div className="flex items-center justify-center gap-2 px-4 pt-4 pb-2 border-b border-white/5">
        <Crown size={16} className="text-amber-400 shrink-0" />
        <div className="text-center">
          <p className="text-[11px] font-black text-amber-400 uppercase tracking-widest leading-tight">
            Craque ou Bagre?
          </p>
          <p className="text-[9px] text-white/40 mt-0.5">Intervalo — vote agora!</p>
        </div>
        <Fish size={16} className="text-rose-400 shrink-0" />
      </div>

      {/* Jogador eleito craque pela IA */}
      <div className="flex flex-col items-center gap-3 px-4 pt-4 pb-3">
        <CardCraque jogador={dados.craque} />
        <p className="text-[10px] text-white/40 text-center leading-snug px-2">
          A análise ao vivo elegeu{' '}
          <span className="text-amber-400 font-bold">{dados.craque.nome}</span>{' '}
          como o melhor do 1º tempo. Você concorda?
        </p>
      </div>

      {/* Botões de voto */}
      {!votado ? (
        <div className="flex gap-3 px-4 pb-4">
          <button
            onClick={() => votar('concorda')}
            className="animate-mcb-pulse flex-1 rounded-xl py-3 font-black text-sm uppercase tracking-wide active:scale-95 transition-transform flex items-center justify-center gap-1.5"
            style={{
              background: 'linear-gradient(145deg, #065f46, #0369a1)',
              boxShadow:  '0 4px 20px rgba(5,150,105,0.35)',
              color:      'white',
            }}
          >
            <Crown size={14} fill="currentColor" strokeWidth={0} />
            É Craque!
          </button>
          <button
            onClick={() => votar('discorda')}
            className="flex-1 rounded-xl py-3 font-black text-sm uppercase tracking-wide active:scale-95 transition-transform flex items-center justify-center gap-1.5"
            style={{
              background: 'linear-gradient(145deg, #7f1d1d, #9a3412)',
              boxShadow:  '0 4px 20px rgba(220,38,38,0.3)',
              color:      'white',
            }}
          >
            <Fish size={14} />
            É Bagre!
          </button>
        </div>
      ) : (
        <div className="px-4 pb-4">
          <div
            className="rounded-xl px-4 py-3 text-center"
            style={{
              background: votado === 'concorda' ? 'rgba(5,150,105,0.12)' : 'rgba(220,38,38,0.12)',
              border:     `1px solid ${votado === 'concorda' ? 'rgba(5,150,105,0.3)' : 'rgba(220,38,38,0.3)'}`,
            }}
          >
            <p className="text-xs font-bold text-white/80 mb-2 flex items-center justify-center gap-1.5">
              {votado === 'concorda'
                ? <><Crown size={12} fill="currentColor" strokeWidth={0} className="text-amber-400" /> Você acha que é Craque!</>
                : <><Fish size={12} className="text-rose-400" /> Você acha que é Bagre!</>
              }
            </p>
            <div className="flex h-3 rounded-full overflow-hidden mb-1.5">
              <div className="bg-emerald-500 transition-all duration-500" style={{ width: `${pctConcorda}%` }} />
              <div className="bg-rose-500 flex-1 transition-all duration-500" />
            </div>
            <div className="flex justify-between text-[9px] font-bold">
              <span className="text-emerald-400">{pctConcorda}%</span>
              <span className="text-white/20">{totalVotos} votos</span>
              <span className="text-rose-400">{pctDiscorda}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Também mostra o bagre */}
      <div
        className="flex items-center gap-2 px-4 py-2 mx-4 mb-4 rounded-xl"
        style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)' }}
      >
        <FotoJogador src={dados.bagre.fotoUrl} nome={dados.bagre.nome} size={36} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-rose-400 truncate flex items-center gap-1">
            <Fish size={10} /> Bagre: {dados.bagre.nome}
          </p>
          <NotaStars nota={dados.bagre.nota} />
        </div>
      </div>
    </div>
  );
}
